import { createContext, useContext, useEffect, useState, useRef } from "react";
import { io } from "socket.io-client";
import { useUserAuth } from "./UserAuthContext";
import api from "../utils/api/axiosInstance";
import { SOCKET_URL } from "../config/env";

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const { user } = useUserAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!user) {
      // Disconnect if user logs out
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Same-origin when SOCKET_URL / API base are relative (production nginx).
    const socketUrl =
      SOCKET_URL ||
      api?.defaults?.baseURL?.replace(/\/api\/?$/, "") ||
      window.location.origin;
    const newSocket = io(socketUrl || window.location.origin, {
      withCredentials: true,
      transports: ["websocket", "polling"],
      auth: { scope: "user" },
    });

    socketRef.current = newSocket;

    newSocket.on("connect", () => {
      setIsConnected(true);
    });

    newSocket.on("disconnect", () => {
      setIsConnected(false);
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket.io connection error:", error);
      setIsConnected(false);
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [user]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};
